import { useIntegrationsStore } from './integrations-store';
import type { IntegrationConfig } from './integrations-store.type';

/**
 * Sets the current integration in the integrations store.
 *
 * @param {Integration | null} integration - The integration to set, or null to clear
 * @return {void}
 */
const setIntegration = (integration: IntegrationConfig | null): void => {
  useIntegrationsStore.setState({ integration }, false, '[Integrations Store] setIntegration');
};

/**
 * Updates the current integration in the integrations store with the provided partial integration data.
 *
 * @param {Partial<IntegrationConfig>} partialIntegration - Partial integration data to update the current integration with.
 * @return {void}
 */
const updateIntegration = (partialIntegration: Partial<IntegrationConfig>): void => {
  const currentIntegration = useIntegrationsStore.getState().integration;

  if (currentIntegration) {
    useIntegrationsStore.setState(
      { integration: { ...currentIntegration, ...partialIntegration } },
      false,
      '[Integrations Store] updateIntegration',
    );
  }
};

/**
 * Sets the re-authentication flag in the integrations store.
 * Used to indicate that the OAuth flow is for re-authentication (not initial setup).
 *
 * @param {boolean} isReauth - True for re-authentication flow, false for initial setup
 * @return {void}
 */
const setIsReauth = (isReauth: boolean): void => {
  useIntegrationsStore.setState({ isReauth }, false, '[Integrations Store] setIsReauth');
};

/**
 * Clears the re-authentication flag (sets to false).
 *
 * @return {void}
 */
const clearIsReauth = (): void => {
  useIntegrationsStore.setState({ isReauth: false }, false, '[Integrations Store] clearIsReauth');
};

/**
 * Sets the active step for the JDC wizard.
 *
 * @param {number} activeStep - The step number to set as active
 * @return {void}
 */
const setJDCActiveStep = (activeStep: number): void => {
  const currentJDCWizard = useIntegrationsStore.getState().jdcWizard ?? { activeStep: 0 };
  useIntegrationsStore.setState(
    { jdcWizard: { ...currentJDCWizard, activeStep } },
    false,
    '[Integrations Store] setJDCActiveStep',
  );
};

export { clearIsReauth, setIntegration, setIsReauth, setJDCActiveStep, updateIntegration };
